import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Stack, Typography, Paper, TextField, FormControlLabel, Switch, Backdrop, CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { Container, LoadingState, ErrorState } from '@/components/common';
import { useQuestionDetail, useUpdateQuestion } from '@/hooks/aiReview.hook';
import { successToast, errorToast } from '@/common/toast';
import { getExtensionFromLanguage } from './utils';
import { FileExplorer, ProjectCodeViewer, PromptTabsEdit } from './components';

/**
 * 프로젝트 개발훈련 수정 페이지
 */

const ProjectEditPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const languageId = searchParams.get('languageId');
  const [activeFileIdx, setActiveFileIdx] = useState(null);
  const [editData, setEditData] = useState(null);

  const { data: project, isLoading, error } = useQuestionDetail(projectId);
  const updateQuestionMutation = useUpdateQuestion();
  const isSaving = updateQuestionMutation.isPending;

  useEffect(() => {
    if (!isSaving) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSaving]);

  const files = editData?.files ?? [];
  const prompts = editData?.prompts ?? editData?.prompt ?? [];
  const activeFile = useMemo(
    () => (activeFileIdx != null && files[activeFileIdx] ? files[activeFileIdx] : files[0]),
    [files, activeFileIdx]
  );

  useEffect(() => {
    if (files.length > 0 && activeFileIdx === null) {
      setActiveFileIdx(0);
    }
  }, [files.length, activeFileIdx]);

  useEffect(() => {
    if (project) {
      const detailData = project?.data ?? project;
      const rawFiles = detailData.files ?? [];
      const normalizedFiles = rawFiles.map((f) => ({
        ...f,
        fileContent: f.fileContent ?? f.content ?? '',
        readonly: f.readonly ?? f.isReadonly ?? false,
      }));
      setEditData({
        title: detailData.title ?? detailData.name ?? detailData.question ?? '',
        description: detailData.description ?? detailData.summary ?? '',
        languageType: detailData.languageType ?? '',
        files: normalizedFiles,
        prompts: detailData.prompts ?? detailData.prompt ?? [],
      });
    }
  }, [project]);

  const updateField = useCallback((field, value) => {
    setEditData((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const updatePrompt = useCallback((index, field, value) => {
    setEditData((prev) => {
      if (!prev) return prev;
      const list = [...(prev.prompts ?? prev.prompt ?? [])];
      if (list[index]) {
        list[index] = { ...list[index], [field]: value };
      }
      return { ...prev, prompts: list };
    });
  }, []);

  const addFile = useCallback(() => {
    const ext = getExtensionFromLanguage(editData?.languageType);
    const newFile = {
      id: `new-${Date.now()}`,
      fileName: `파일${(files.length + 1).toString()}.${ext}`,
      fileContent: '',
      readonly: false,
    };
    setEditData((prev) => (prev ? {
      ...prev,
      files: [...(prev.files ?? []), newFile],
    } : prev));
    setActiveFileIdx(files.length);
  }, [files.length, editData?.languageType]);

  const removeFile = useCallback((index) => {
    setEditData((prev) => (prev ? {
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    } : prev));
    setActiveFileIdx((prev) => {
      const nextLength = files.length - 1;
      if (nextLength <= 0) return null;
      if (prev === index) return 0;
      if (prev > index) return prev - 1;
      return prev;
    });
  }, [files.length]);

  const updateFile = useCallback((index, field, value) => {
    setEditData((prev) => {
      if (!prev?.files) return prev;
      const list = [...prev.files];
      if (list[index]) {
        const key = field === 'content' ? 'fileContent' : field === 'isReadonly' ? 'readonly' : field;
        list[index] = { ...list[index], [key]: value };
      }
      return { ...prev, files: list };
    });
  }, []);

  const updateFileContent = useCallback((index, content) => {
    setEditData((prev) => {
      if (!prev?.files) return prev;
      const list = [...prev.files];
      if (list[index]) {
        list[index] = { ...list[index], fileContent: content ?? '' };
      }
      return { ...prev, files: list };
    });
  }, []);

  const handleEditableChange = useCallback((index, isEditable) => {
    if (isEditable) {
      setEditData((prev) => {
        if (!prev?.files) return prev;
        return {
          ...prev,
          files: (prev.files ?? []).map((f, i) => ({
            ...f,
            readonly: i !== index,
          })),
        };
      });
    } else {
      updateFile(index, 'isReadonly', true);
    }
  }, [updateFile]);

  const handleApplyFirstToSecond = useCallback(() => {
    setEditData((prev) => {
      const list = [...(prev.prompts ?? prev.prompt ?? [])];
      const sorted = [...list].sort((a, b) => (a.attemptOrder ?? a.id) - (b.attemptOrder ?? b.id));
      const first = sorted[0];
      const second = sorted[1];
      if (!first || !second) return prev;
      const criteria = first.promptEvaluationCriteria ?? first.judgmentCriteria ?? '';
      const fewShot = first.promptFewShot ?? first.fewShotExamples ?? '';
      const secondIdx = list.indexOf(second);
      if (secondIdx === -1) return prev;
      list[secondIdx] = {
        ...list[secondIdx],
        promptEvaluationCriteria: criteria,
        fewShotExamples: fewShot,
        promptFewShot: fewShot,
      };
      return { ...prev, prompts: list };
    });
    successToast('1차 프롬프트 내용이 2차 프롬프트에도 적용되었습니다.');
  }, []);

  const onSubmit = useCallback(async () => {
    if (!editData) return;
    const payload = {
      ...editData,
      prompts: editData.prompts?.map((p) => ({
        ...(p.id && { id: p.id }),
        attemptOrder: p.attemptOrder,
        model: p.model ?? null,
        judgmentCriteria: p.promptEvaluationCriteria ?? '',
        fewShotExamples: p.promptFewShot ?? '',
        temperature: p.temperature != null ? Number(p.temperature) : null,
        maxTokens: p.maxTokens != null ? Number(p.maxTokens) : null,
      })),
      files: editData.files?.map((f) => ({
        ...(f.id && !String(f.id).startsWith('new-') && { id: f.id }),
        fileName: f.fileName,
        content: f.fileContent ?? '',
        isReadonly: f.readonly ?? false,
      })) ?? [],
    };

    try {
      console.log(payload);
      await updateQuestionMutation.mutateAsync({ questionId: projectId, ...payload });
      successToast('프로젝트가 수정되었습니다.');
      const query = languageId ? `?languageId=${languageId}` : '';
      navigate(`/education/projects/detail/${projectId}${query}`);
    } catch (err) {
      errorToast(`프로젝트 수정에 실패했습니다. - ${err.response?.data?.msg}`);
    }
  }, [editData, updateQuestionMutation, navigate, projectId, languageId]);

  if (isLoading) {
    return <LoadingState title="프로젝트 로딩 중" />;
  }

  if (error || !project) {
    return (
      <ErrorState
        title="프로젝트 로딩 오류"
        message="프로젝트 정보를 불러오는 중 오류가 발생했습니다."
        onGoBack={() => {
          const query = languageId ? `?languageId=${languageId}` : '';
          navigate(`/education/projects${query}`);
        }}
      />
    );
  }

  if (!editData) {
    return <LoadingState title="프로젝트 로딩 중" />;
  }

  return (
    <Container title="프로젝트 수정">
      <Backdrop open={isSaving} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: 'column', gap: 2, bgcolor: 'rgba(0,0,0,0.6)' }}>
        <CircularProgress color="inherit" sx={{ color: '#fff' }} />
        <Typography variant="body1" color="#fff">
          파일을 저장하고 사전 컴파일 중입니다. 잠시만 기다려 주세요.
        </Typography>
      </Backdrop>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            if (isSaving && !window.confirm('저장이 진행 중입니다. 페이지를 벗어나면 결과를 확인할 수 없습니다. 이동하시겠습니까?')) return;
            const query = languageId ? `?languageId=${languageId}` : '';
            navigate(`/education/projects/detail/${projectId}${query}`);
          }}
        >
          상세로
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSubmit}
          disabled={isSaving}
        >
          저장
        </Button>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          기본 정보
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="제목"
            fullWidth
            required
            value={editData.title}
            onChange={(e) => updateField('title', e.target.value)}
          />
          <TextField
            label="설명"
            fullWidth
            multiline
            rows={3}
            value={editData.description}
            onChange={(e) => updateField('description', e.target.value)}
          />
          <TextField
            label="언어 타입"
            fullWidth
            value={editData.languageType}
            onChange={(e) => updateField('languageType', e.target.value)}
            placeholder="예: java"
          />
        </Stack>
      </Paper>

      <PromptTabsEdit prompts={prompts} onUpdatePrompt={updatePrompt} handleApplyFirstToSecond={handleApplyFirstToSecond} />

      <Paper sx={{ overflow: 'hidden', mb: 2 }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            파일
          </Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addFile}>
            파일 추가
          </Button>
        </Box>
      </Paper>

      {files.length > 0 ? (
        <FileExplorer
          files={files}
          activeKey={activeFileIdx}
          selectionMode="index"
          mode="edit"
          headerTitle="파일"
          onSelectFile={setActiveFileIdx}
          onRemoveFile={removeFile}
        >
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="파일명"
              value={activeFile?.fileName ?? ''}
              onChange={(e) => updateFile(activeFileIdx ?? 0, 'fileName', e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={activeFile ? !(activeFile.readonly ?? activeFile.isReadonly) : false}
                  onChange={(e) => {
                    if (activeFileIdx == null) return;
                    handleEditableChange(activeFileIdx, e.target.checked);
                  }}
                />
              }
              label="수정 가능"
              labelPlacement="start"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.813rem' } }}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 600 }}>
            <ProjectCodeViewer
              key={activeFile?.id ?? activeFileIdx}
              fileName={activeFile?.fileName ?? ''}
              content={activeFile?.fileContent ?? activeFile?.content ?? ''}
              readOnly={false}
              onChange={(value) => updateFileContent(activeFileIdx ?? 0, value ?? '')}
            />
          </Box>
        </FileExplorer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            등록된 파일이 없습니다. &quot;파일 추가&quot; 버튼으로 파일을 추가하세요.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addFile}>
            파일 추가
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default ProjectEditPage;
